export interface TestEducation {
    title: string;
    description: string;
    highLevel?: {
        title: string;
        description: string;
        symptoms?: string[];
    };
    lowLevel?: {
        title: string;
        description: string;
        symptoms?: string[];
    };
    normalLevel?: {
        title: string;
        description: string;
    };
    additionalInfo?: string;
    sources?: string[];
}

export const TEST_EDUCATION: Record<string, TestEducation> = {
    magnesium: {
        title: 'About Magnesium',
        description:
            'Magnesium is a mineral stored in your bones, tissues and cells that is important in producing energy, muscle and nerve function, heart health and bone strength. You absorb magnesium into your system through your small intestines when you eat foods such as spinach, whole grains, nuts, beans, bananas, milk, and salmon.',
        lowLevel: {
            title: 'Low Magnesium Level',
            description:
                'Low magnesium levels can indicate inefficient mineral absorption often caused by excessive alcohol consumption, underlying kidney problems, certain medications, celiac disease or chronic digestive issues. Over time, magnesium deficiency can lead to decreased calcium and potassium levels, weak bones and negative effects on your heart.',
            symptoms: [
                'Nausea and vomiting',
                'Sleepiness',
                'Headaches',
                'Weakness and fatigue',
                'Loss of appetite',
                'Muscle spasms',
            ],
        },
        highLevel: {
            title: 'High Magnesium Level',
            description:
                'While not as common as low levels, high magnesium levels are typical in those with kidney damage or those who take certain drugs. Excessive levels of magnesium can cause serious complications, including cardiac arrest.',
        },
    },

    folate: {
        title: 'About Folate',
        description:
            'Folate (vitamin B-9) is important in red blood cell formation for healthy cell growth and function. This nutrient is crucial during early pregnancy to reduce the risk of birth defects of the brain and spine. There is evidence folate can reduce the risk of various cancers and is helpful in treating depression.',
        highLevel: {
            title: 'High Folate Levels',
            description:
                "While excess folate levels aren't usually dangerous, they are linked to some conditions, including certain types of cancers.",
        },
        lowLevel: {
            title: 'Low folate levels',
            description:
                'could be caused by the following: high alcohol consumption, diet low in folate, genetic factors and certain medications.',
            symptoms: ['Fatigue', 'Gray hair', 'Mouth sores', 'Tongue swelling', 'Growth problems'],
        },
        additionalInfo:
            'For most, a balanced diet usually provides all the folate you need. Folate is found in dark green leafy vegetables, beans, peas, nuts, oranges, lemons, bananas, melons, and strawberries. The synthetic form of folate is folic acid and is in many fortified foods, such as cereals and pastas.',
    },

    ferritin: {
        title: 'About Ferritin',
        description:
            "Ferritin is a blood protein that stores iron. Testing your ferritin levels can indicate if your body's iron levels are high or low.",
        lowLevel: {
            title: 'Low ferritin levels',
            description:
                "in your blood indicate your body's iron stores are low and you have iron deficiency which could mean you are anemic.",
            symptoms: [
                'Weakness',
                'Dizziness',
                'Pale skin',
                'Headache',
                'Numbness or coldness in hands and feet',
                'Shortness of breath on exertion',
                'Low body temperature',
            ],
        },
        highLevel: {
            title: 'High ferritin Levels',
            description:
                'indicate that your body is storing too much iron. This could point to liver disease, rheumatoid arthritis, and other inflammatory conditions or hyperthyroidism. Some types of cancer also cause your blood ferritin level to be high.',
        },
    },

    'uric acid': {
        title: 'About Uric Acid',
        description:
            'Uric acid is a normal body waste product formed when chemicals called purines break down. Purines are a natural substance found in the body. When purines are broken down to uric acid in the blood, the body rids it through urination or bowel movements. If your body makes too much uric acid, it can build up in the blood. Uric acid levels can increase when you eat too many high-purine foods such as liver, shellfish and alcohol or if you take medicines like diuretics, aspirin, and niacin. Crystals of uric acid can form and collect in the joints, causing painful inflammation such as gout and can also lead to kidney stones.',
        highLevel: {
            title: 'High levels of uric acid symptoms could include:',
            description: '',
            symptoms: [
                'Severe pain in your joints',
                'Joint stiffness',
                'Difficulty moving affected joints',
                'Redness and swelling',
                'Misshapen joints',
            ],
        },
    },

    'vitamin b12': {
        title: 'About Vitamin B12',
        description:
            'Vitamin B-12 (cobalamin) is an essential vitamin and plays an important role in blood cell formation, cell metabolism, nerve function and the production of DNA. It is found in foods such as meat, fish, dairy, and fortified foods such as breakfast cereal and is widely available as an oral supplement.',
        highLevel: {
            title: 'High Vitamin B12 Levels',
            description:
                'Abnormally high B12 levels may suggest liver or kidney problems, diabetes, or certain forms of leukemia. Megadoses of the vitamin can lead to acne and rosacea.',
        },
        lowLevel: {
            title: 'Low Vitamin B12 Levels',
            description:
                "While vitamin B-12 deficiency is not common in the US, people who follow a vegetarian or vegan diet might be prone to this deficiency because plant foods don't contain vitamin B-12. Older adults and people with digestive tract conditions that affect absorption of nutrients may also be susceptible to vitamin B-12 deficiency.",
            symptoms: [
                'Anemia',
                'Fatigue',
                'Muscle weakness',
                'Intestinal problems',
                'Nerve damage',
                'Mood disturbances',
            ],
        },
    },

    'vitamin d (oh-25)': {
        title: 'About Vitamin D',
        description:
            'Vitamin D is an essential vitamin that helps regulate calcium and phosphorus in the body and is needed for building and maintaining healthy bones. Your body can only absorb calcium, the primary component of bone, when vitamin D is present. It is known as the "sunshine vitamin" because it can be made in the skin with exposure to UV light. Vitamin D also regulates many other cellular functions in your body. Its anti-inflammatory, antioxidant and neuroprotective properties support immune health, muscle function and brain cell activity.',
        lowLevel: {
            title: 'Low Vitamin D Levels',
            description:
                'Deficiencies in vitamin D leads to low calcium absorption which causes weakened bones that become soft and brittle and prone to fractures as well as osteoporosis - a loss of total bone mass. Deficiency in vitamin D may contribute to the development of cancer, cardiovascular disease, type 2 diabetes, and autoimmune disorders.',
            symptoms: [
                'Fatigue',
                'Bone pain',
                'Muscle weakness, muscle aches or muscle cramps',
                'Mood changes like depression',
            ],
        },
        highLevel: {
            title: 'High Vitamin D Levels',
            description:
                'While rare, high levels of vitamin D are usually caused by large doses of vitamin D supplements - not by diet or sun exposure. The buildup of vitamin D in your blood can cause nausea, vomiting, weakness and frequent urination.',
        },
    },

    cholesterol: {
        title: 'About Total Cholesterol',
        description:
            'Total cholesterol is the total amount of cholesterol in your blood. It includes low-density lipoprotein (LDL, or "bad") cholesterol and high-density lipoprotein (HDL, or "good") cholesterol. Cholesterol is a waxy, fat-like substance found in every cell in your body. Your total cholesterol level reflects your risk for heart disease. The higher the level, the higher your risk. If your total cholesterol is too high, your healthcare provider may recommend lifestyle changes and/or medications to lower it.',
        highLevel: {
            title: 'Symptoms',
            description:
                "By itself, high blood cholesterol doesn't cause symptoms, which is why it's important to have your cholesterol tested.",
        },
    },

    ldl: {
        title: 'About LDL Cholesterol',
        description:
            'Low-density-lipoprotein (LDL) Is a lipoprotein that transports cholesterol to the cells. High levels of LDLs in the blood have been associated with an increased risk for heart disease. For this reason, they are referred to as "bad cholesterol". LDL cholesterol needs to be kept at the lowest level you and your healthcare provider can achieve. LDL cholesterol goes up if your diet is high in saturated and "trans" fat. Your total cholesterol level reflects your risk for heart disease. The higher the level, the higher your risk. If your total cholesterol is too high, your healthcare provider may recommend lifestyle changes and/or medications to lower it.',
        highLevel: {
            title: 'High LDL Levels',
            description:
                'Increased risk for heart disease. High LDL is primarily caused by poor diet, obesity, lack of exercise, smoking, alcohol and age.',
        },
        lowLevel: {
            title: 'Low LDL Levels',
            description: 'Decreased risk of heart disease.',
        },
        additionalInfo: "High LDL has no symptoms, which is why it's important to have your LDL levels checked.",
    },

    'direct hdl': {
        title: 'About HDL Cholesterol',
        description:
            'High-density lipoprotein (HDL) is a lipoprotein that picks up cholesterol from cells and transports it to the liver so that it can be eliminated from the body. It essentially clears fats from your blood stream. HDL cholesterol is called the "good cholesterol" because high levels of HDL in the blood are associated with a reduction in the risk of heart disease. The higher the HDL the better.',
        highLevel: {
            title: 'High HDL Levels',
            description:
                'Reduced risk of heart disease. Physical activity, losing extra weight, quitting smoking, using alcohol in moderation and choosing better fats all help increase your HDL levels.',
        },
        lowLevel: {
            title: 'Low HDL Levels',
            description:
                'Associated with an increased risk of cardiovascular disease. Type 2 diabetes, being overweight, not getting enough exercise, smoking, unhealthy diet, metabolic syndrome, and genetic factors play a role in low HDL levels.',
        },
        additionalInfo:
            "Low HDL levels rarely present symptoms until after there's been a medical emergency, which is why it's important to have your HDL levels checked.",
    },

    triglycerides: {
        title: 'About Triglycerides',
        description:
            'Triglycerides are the main types of lipid (fat) in food and the body. The structure of triglycerides includes lipid molecules called fatty acids. Fatty acids are what we are really talking about when we refer to trans fat or saturated fat. You have a higher risk of high triglycerides if you have diabetes, chronic kidney disease, metabolic syndrome, inflammatory diseases, obesity or are overweight, consume excess: alcohol, saturated fats, and sugars, or if you maintain a sedentary lifestyle, or have a family history of high triglycerides.',
        highLevel: {
            title: 'High Triglyceride Levels',
            description:
                'High concentrations of triglycerides in the blood may contribute to hardening of the arteries or thickening of the artery walls which increases the risk of stroke, heart attack and heart disease. Extremely high triglycerides can also cause acute inflammation of the pancreas (pancreatitis).',
            symptoms: [
                'In rare cases, some people may develop inflammation of the pancreas which can cause sudden severe abdominal pain, loss of appetite, nausea, vomiting and fever',
            ],
        },
        lowLevel: {
            title: 'Low Triglyceride Levels',
            description:
                'In general, having low triglyceride levels is not a problem. If your levels are less than 150mg/dL, you have a lower risk of cardiovascular diseases such as heart attack or stroke.',
        },
    },

    tsh: {
        title: 'About TSH (Thyroid Stimulating Hormone)',
        description:
            'TSH is made by your pituitary gland and tells your thyroid how much thyroid hormone to make. TSH levels help doctors understand how well your thyroid is working.',
        highLevel: {
            title: 'High TSH Level',
            description:
                "High TSH usually means your thyroid is underactive (hypothyroidism). Your thyroid isn't making enough thyroid hormone, so your pituitary gland makes more TSH to try to stimulate it.",
            symptoms: [
                'Weight gain',
                'Fatigue',
                'Cold sensitivity',
                'Dry skin',
                'Hair loss',
                'Constipation',
                'Depression',
            ],
        },
        lowLevel: {
            title: 'Low TSH Level',
            description:
                'Low TSH usually means your thyroid is overactive (hyperthyroidism). Your thyroid is making too much thyroid hormone, so your pituitary gland makes less TSH.',
            symptoms: [
                'Weight loss',
                'Rapid heartbeat',
                'Nervousness',
                'Heat sensitivity',
                'Sweating',
                'Tremors',
                'Difficulty sleeping',
            ],
        },
    },

    'free t3': {
        title: 'About Free T3 (Triiodothyronine)',
        description:
            "T3, also called triiodothyronine, is a hormone produced by the thyroid. Most of the T3 in your body binds to protein. The T3 that doesn't bind to protein is called T3 free. Free T3 measures the amount of T3 hormone that is available for your body to use and indicates how well the thyroid is functioning.",
        highLevel: {
            title: 'High T3 free levels',
            description: 'causes an excessively high metabolic rate and increased risk of hyperthyroidism.',
            symptoms: [
                'Weight loss',
                'Rapid heartbeat',
                'Nervousness',
                'Heat sensitivity',
                'Sweating',
                'Tremors',
                'Difficulty sleeping',
                'Increased appetite',
            ],
        },
        lowLevel: {
            title: 'Low T3 free levels',
            description:
                'may indicate hypothyroidism, meaning your thyroid is underactive and not producing enough hormone.',
            symptoms: [
                'Weight gain',
                'Fatigue',
                'Cold sensitivity',
                'Dry skin',
                'Hair loss',
                'Constipation',
                'Depression',
                'Memory problems',
            ],
        },
    },

    't3, total': {
        title: 'About T3 Total (Triiodothyronine)',
        description:
            "T3, also called triiodothyronine, is a hormone produced by the thyroid. Most of the T3 in your body binds to protein. The T3 that doesn't bind to protein is called T3 free. The T3 total test measures both bound and free T3 and indicates how well the thyroid is functioning.",
        highLevel: {
            title: 'High T3 total levels',
            description: 'causes an excessively high metabolic rate and increased risk of hyperthyroidism.',
            symptoms: [
                'Weight loss',
                'Rapid heartbeat',
                'Nervousness',
                'Heat sensitivity',
                'Sweating',
                'Tremors',
                'Difficulty sleeping',
            ],
        },
        lowLevel: {
            title: 'Low T3 total levels',
            description:
                'may indicate hypothyroidism, meaning your thyroid is underactive and not producing enough hormone.',
            symptoms: [
                'Weight gain',
                'Fatigue',
                'Cold sensitivity',
                'Dry skin',
                'Hair loss',
                'Constipation',
                'Depression',
            ],
        },
    },

    'free t4': {
        title: 'About Free T4 (Thyroxine)',
        description:
            'T4, also called thyroxine, is the main form of thyroid hormone made by the thyroid gland. T4 is mostly bound to proteins with a small amount unbound, or free. This test measures the amount of free/or unbound T4 circulating through the blood and indicates how well the thyroid gland is functioning.',
        highLevel: {
            title: 'High levels of T4',
            description:
                'High levels of T4 may be caused by certain thyroid problems such as thyroiditis, a goiter, high levels of protein in the blood, and hyperthyroidism.',
            symptoms: ['Anxiety', 'Unplanned weight loss', 'Tremors', 'Diarrhea'],
        },
        lowLevel: {
            title: 'Low levels of T4',
            description:
                'Low levels of T4 may be caused by fasting, malnutrition, iodine deficiency, medications that affect protein levels, illness, pituitary problems, and hypothyroidism.',
            symptoms: [
                'Weight gain',
                'Fatigue',
                'Hair loss',
                'Low tolerance for cold temperatures',
                'Irregular period',
                'Constipation',
            ],
        },
    },

    hba1c: {
        title: 'About HbA1c (Hemoglobin A1c)',
        description:
            "This blood test measures your average blood sugar levels. It shows the amount of glucose attached to hemoglobin (which is the part of your red blood cells that carries oxygen from your lungs to the rest of your body). It's used to help detect and diagnose prediabetes and diabetes. If you have diabetes, this test helps you and your doctor manage it.",
        highLevel: {
            title: 'High HbA1c levels',
            description: 'High HbA1c levels may be a sign of diabetes, or means you are at risk for getting diabetes.',
            symptoms: [
                'Increased thirst',
                'Increased urination',
                'Blurred vision',
                'Fatigue',
                'Anxiety and panic',
                'Lack of motivation',
            ],
        },
        lowLevel: {
            title: 'Low HbA1c levels',
            description:
                'Low HbA1c levels can be caused by conditions that decrease red blood cell levels, such as alcohol, liver disease, genetic disorders and some drugs and supplements.',
            symptoms: [
                'Nervousness',
                'Dizziness',
                'Trembling',
                'Sweating',
                'Hunger',
                'Weakness',
                'Severe cases may lead to seizures and loss of consciousness',
            ],
        },
    },

    insulin: {
        title: 'About Insulin',
        description:
            'Insulin is a hormone made by your pancreas that helps glucose (sugar) from food get into your cells to be used for energy.',
        highLevel: {
            title: 'High Insulin Level',
            description:
                'High insulin levels may indicate insulin resistance, which can lead to type 2 diabetes, or reactive hypoglycemia.',
            symptoms: [
                'Weight gain, especially around the waist',
                'Fatigue',
                'Hunger',
                'Difficulty concentrating',
                'High blood pressure',
                'High cholesterol',
            ],
        },
        lowLevel: {
            title: 'Low Insulin Level',
            description:
                "Low insulin levels may indicate type 1 diabetes, where the pancreas doesn't make enough insulin.",
        },
    },

    glucose: {
        title: 'About Glucose',
        description:
            "Glucose is a type of sugar that is your body's main source of energy. Your body gets glucose from the food you eat.",
        highLevel: {
            title: 'High Glucose Level',
            description: 'High blood glucose levels may indicate diabetes or prediabetes.',
            symptoms: ['Increased thirst', 'Frequent urination', 'Fatigue', 'Blurred vision', 'Slow wound healing'],
        },
        lowLevel: {
            title: 'Low Glucose Level',
            description:
                'Low blood glucose (hypoglycemia) can be dangerous and may cause symptoms like dizziness, confusion, and fainting.',
        },
    },

    estradiol: {
        title: 'About Estradiol',
        description:
            'Estradiol is the strongest of the three estrogens in the body and is an important factor in the female reproductive system. While men and women have estradiol, women have much higher levels of the hormone than men.',
        normalLevel: {
            title: 'Normal Function',
            description:
                "In women: Estradiol's main function is to mature and then maintain the reproductive system. Estradiol levels cause the maturation and release of the egg as well as the thickening of the uterus lining to allow a fertilized egg to implant. As women age, levels of this hormone decrease significantly.\n\nIn men: Proper estradiol levels help with bone maintenance, nitric oxide production, and brain function.",
        },
        highLevel: {
            title: 'High Estradiol Level',
            description: 'High estradiol levels can cause various symptoms.',
            symptoms: [
                'In women: Acne, constipation, loss of sex drive, depression, increased risk of uterine and breast cancer, increased risk of cardiovascular disease, weight gain, menstrual problems',
                'In men: Development of female characteristics, mood swings',
            ],
        },
        lowLevel: {
            title: 'Low Estradiol Level',
            description: 'Low estradiol levels can cause various symptoms.',
            symptoms: ['In women: Absence of menstrual cycle, mood swings, vaginal dryness, hot flashes'],
        },
        additionalInfo: `Reference Ranges:

Women (premenopausal):
• Early follicular phase: 20.0 – 157 pg/mL
• Mid-follicular phase: 20.7 – 139 pg/mL
• Ovulation peak: 19.9 – 622 pg/mL
• Mid-luteal phase: 25.8 – 279 pg/mL

Women (postmenopausal):
• < 15 – 30 pg/mL

Men (adult):
• 11 – 44 pg/mL`,
    },

    cortisol: {
        title: 'About Cortisol',
        description:
            'Cortisol is a steroid hormone produced by your adrenal glands. It helps your body respond to stress, fight infection, and regulate blood sugar.',
        highLevel: {
            title: 'High Cortisol Level',
            description:
                "High cortisol levels can be caused by stress, certain medications, or disorders like Cushing's syndrome.",
            symptoms: [
                'Weight gain, especially around the midsection',
                'High blood pressure',
                'Muscle weakness',
                'Mood swings',
                'Difficulty sleeping',
                'High blood sugar',
            ],
        },
        lowLevel: {
            title: 'Low Cortisol Level',
            description: "Low cortisol levels may indicate Addison's disease or other adrenal insufficiency disorders.",
            symptoms: ['Fatigue', 'Weight loss', 'Low blood pressure', 'Muscle weakness', 'Depression'],
        },
    },

    tpoab: {
        title: 'About TPOAb (Thyroid Peroxidase Antibodies)',
        description:
            'TPOAb is a test that measures antibodies against thyroid peroxidase, an enzyme important for thyroid hormone production. High levels may indicate an autoimmune thyroid condition.',
        highLevel: {
            title: 'High TPOAb Level',
            description:
                "Elevated TPOAb levels suggest an autoimmune thyroid condition such as Hashimoto's thyroiditis or Graves' disease.",
            symptoms: [
                'Fatigue',
                'Weight changes',
                'Hair loss',
                'Mood changes',
                'Temperature sensitivity',
                'Sleep disturbances',
            ],
        },
        normalLevel: {
            title: 'Normal TPOAb Level',
            description: 'Normal TPOAb levels suggest no significant autoimmune activity against the thyroid gland.',
        },
    },

    homocysteine: {
        title: 'About Homocysteine',
        description:
            'Homocysteine is an amino acid produced when proteins are broken down. Normally, vitamin B12, vitamin B6 and folic acid break down homocysteine and change it into other substances your body needs. After this break down, there should be very little homocysteine left in the bloodstream.',
        highLevel: {
            title: 'High Homocysteine Levels',
            description:
                'Also called hyperhomocysteinemia, can contribute to arterial damage and blood clots in your blood vessels. High homocysteine levels usually indicate a deficiency in vitamin B-12 or folate.',
            symptoms: [
                'Pale skin',
                'Weakness',
                'Fatigue',
                'Tingling sensations in hands, arms, legs, or feet',
                'Dizziness',
                'Mouth sores',
                'Mood changes',
            ],
        },
        additionalInfo:
            'Increasing folate, vitamin B6 or B12 in your diet can help lower homocysteine levels. Foods such as beans, leafy green vegetables, citrus fruits, beets, and wheat germ which are high in folic acid can help.',
    },

    'vldl-c': {
        title: 'About VLDL Cholesterol',
        description:
            'VLDL-C stands for very-low-density lipoprotein cholesterol. Your liver makes VLDL and releases it into your bloodstream. The VLDL particles mainly carry triglycerides (a type of fat) to your tissues. It\'s similar to LDL cholesterol, but LDL mainly carries cholesterol to your tissues whereas VLDL-C carries triglycerides to your tissues. You can lower your VLDL level by losing weight, dieting, and exercising. It\'s important to switch to healthy fats and cut back on sugar and alcohol. Some people may need to take medication. VLDL is another "bad cholesterol" because it contributes to the buildup of plaque in your arteries. Over time the plaque can harden and narrow your arteries and contribute to a type of blood vessel disease called atherosclerosis, which reduces blood flow.',
        highLevel: {
            title: 'High VLDL-C Levels',
            description:
                'Increased risk for heart disease and stroke. High VLDL is primarily caused by poor diet, obesity, lack of exercise, smoking, alcohol and age.',
            symptoms: ['Tired', 'Shortness of breath', 'Possible chest pain'],
        },
        lowLevel: {
            title: 'Low VLDL-C Levels',
            description: 'Decreased risk of heart disease.',
        },
    },

    'cholesterol/hdl ratio': {
        title: 'About Cholesterol/HDL Ratio',
        description:
            'This is the ratio of cholesterol to direct HDL. The cholesterol/HDL ratio is calculated by dividing your total cholesterol by your HDL cholesterol. This ratio helps assess your risk of heart disease.',
        highLevel: {
            title: 'High Ratio',
            description:
                'A high cholesterol/HDL ratio indicates increased risk of heart disease. The goal is to keep this ratio as low as possible.',
        },
    },

    fsh: {
        title: 'About FSH (Follicle Stimulating Hormone)',
        description:
            "FSH stands for follicle stimulating hormone. This hormone is secreted by the anterior pituitary gland which helps control a woman's menstrual cycle and egg production and a man's sperm production. Too much or too little FSH can cause a variety of problems in men and women.",
        highLevel: {
            title: 'High FSH Levels',
            description:
                "In men: can be caused by damage due to chemotherapy, radiation, infection, or alcohol abuse. In women: can be caused by menopause and polycystic ovarian syndrome in which a women's hormones are out of balance.",
        },
        lowLevel: {
            title: 'Low FSH Levels',
            description:
                'In men: is commonly caused by use of testosterone or steroids. In women: may indicate problems with the pituitary gland or hypothalamus.',
        },
        additionalInfo: `Reference Ranges:

Women (premenopausal):
• Follicular phase: 3.0 – 8.0 mIU/mL
• Ovulatory peak: 2.6 – 16.9 mIU/mL
• Luteal phase: 1.4 – 5.5 mIU/mL

Women (postmenopausal):
• 26 – 135 mIU/mL (FSH rises significantly after menopause)

Men (adult):
• 1.5 – 12.4 mIU/mL`,
    },

    lh: {
        title: 'About LH (Luteinizing Hormone)',
        description:
            'Luteinizing hormone is produced and released in the anterior pituitary gland. This hormone plays a significant role in controlling the production of estrogen in the ovaries in females and testosterone in the testes in males. LH plays an important role in sexual development and functioning. Too much or too little LH can cause a variety of problems including delayed puberty in children, infertility, menstrual difficulties in women, and low sex drive in men.',
        highLevel: {
            title: 'High LH Levels',
            description:
                'In men: can be caused by damage due to chemotherapy, radiation, infection, or alcohol abuse. It may be an indicator of testicular failure. In women: may indicate menopause or polycystic ovarian syndrome.',
        },
        lowLevel: {
            title: 'Low LH Levels',
            description:
                'May indicate problems with the pituitary gland or hypothalamus, which can affect fertility and sexual function.',
        },
        additionalInfo: `Reference Ranges:

Women (premenopausal):
• Follicular phase: 2.4 – 12.6 mIU/mL
• Ovulation peak: 14.0 – 95.6 mIU/mL
• Luteal phase: 1.0 – 11.4 mIU/mL

Women (postmenopausal):
• 7.7 – 58.5 mIU/mL

Men (adult):
• 1.7 – 8.6 mIU/mL`,
    },

    progesterone: {
        title: 'About Progesterone',
        description:
            'Progesterone is a hormone produced primarily by the ovaries in women and in smaller amounts by the adrenal glands in both men and women. It plays a crucial role in the menstrual cycle, pregnancy, and embryogenesis.',
        highLevel: {
            title: 'High Progesterone Levels',
            description: 'May indicate pregnancy, ovarian cysts, or adrenal gland disorders.',
        },
        lowLevel: {
            title: 'Low Progesterone Levels',
            description: 'May indicate problems with ovulation, miscarriage risk, or menstrual irregularities.',
            symptoms: ['Irregular menstrual periods', 'Difficulty getting pregnant', 'Mood changes', 'Sleep problems'],
        },
        additionalInfo: `Reference Ranges:

Women (premenopausal):
• Follicular phase: 0.1 – 0.9 ng/mL
• Luteal phase: 1.8 – 23.9 ng/mL
• Ovulation peak: 0.1 – 12.0 ng/mL

Women (postmenopausal):
• 0.0 – 0.1 ng/mL

Men:
• 0.0 – 0.2 ng/mL`,
    },

    iron: {
        title: 'About Iron',
        description:
            'Iron is an essential nutrient that is required for the production of healthy red blood cells (RBC) which binds oxygen in the lungs and releases it as blood circulates to other parts of the body.',
        highLevel: {
            title: 'High iron levels',
            description:
                'iron overload, or hemochromatosis is a condition in which your body stores too much iron. Excess iron can build up in the heart, liver, joints, pancreas, and pituitary glands. Untreated, it can cause organ damage, and lead to a heart attack.',
            symptoms: [
                'Joint pain',
                'Abdominal pain',
                'Fatigue',
                'Weakness',
                'Diabetes',
                'Loss of sex drive',
                'Heart failure',
                'Impotence',
            ],
        },
        lowLevel: {
            title: 'Low iron levels',
            description:
                "or anemia, is when your body doesn't have enough red blood cells. The red blood cells carry oxygen from your lungs to the rest of your body. Every organ and tissue in your body needs oxygen to work properly.",
            symptoms: [
                'Fatigue',
                'Weakness',
                'Pale skin',
                'Cold hands and feet',
                'Brittle nails',
                'Strange cravings for non-food items',
                'Restless legs syndrome',
            ],
        },
    },

    'total testosterone': {
        title: 'About Total Testosterone',
        description:
            'Total Testosterone measures the level of the hormone testosterone in your blood. Testosterone is a male sex hormone that helps male features develop over time. Women also have this hormone as it is made in their ovaries and adrenal glands. Testosterone cause physical changes in boys during pubecent years. Men continue to make testosterone. It helps make sperm and helps to boost sex drive.',
        highLevel: {
            title: 'High Testosterone Levels',
            description: 'High testosterone levels can cause different symptoms in men and women.',
            symptoms: [
                'In women: Acne or oily skin, worsened sleep apnea, increase in muscle mass, excess hair growth on face and body, anxiety and depression, deepening of the voice, enlarged clitoris, infertility',
                'In men: Acne, aggressive behavior, high sex drive, headaches, increased appetite, high blood pressure, excessive body hair, heart or liver problems',
            ],
        },
        lowLevel: {
            title: 'Low Testosterone Levels',
            description: 'Low testosterone levels can cause different symptoms in men and women.',
            symptoms: [
                'In men: Large breasts, low sex drive, low sperm count, changes in testicles, weakening of bones, trouble getting an erection, loss of muscle, hair loss',
                'In women: Fertility problems, irregular periods, low sex drive, muscle weakness',
            ],
        },
    },

    'free testosterone': {
        title: 'About Free Testosterone',
        description:
            '98% of the testosterone found in the body is bound to either sex-hormone binding globulin (SHBG) or albumin. This is referred to as "bound testosterone". The other 2% that is left is known as "free testosterone". This free testosterone is what connects with testosterone receptors within the body\'s cells. Free testosterone enables cell replication in the bones and muscles and is responsible for secondary sexual characteristics in men such as facial hair and a deeper voice. Low levels of free testosterone can lead to poor muscle development, irritability, low sex drive and other issues.',
    },

    't4, total': {
        title: 'About T4, Total (Thyroxine)',
        description:
            'T4, also called thyroxine, is the main form of thyroid hormone made by the thyroid gland. T4 is mostly bound to proteins with a small amount unbound, or free. This test measures the total amount of both free and bound T4 and helps identify thyroid dysfunction.',
        highLevel: {
            title: 'High levels of T4',
            description:
                'High levels of T4 may be caused by certain thyroid problems such as thyroiditis, a goiter, high levels of protein in the blood, and hyperthyroidism.',
            symptoms: ['Anxiety', 'Unplanned weight loss', 'Tremors', 'Diarrhea'],
        },
        lowLevel: {
            title: 'Low levels of T4',
            description:
                'Low levels of T4 may be caused by fasting, malnutrition, iodine deficiency, medications that affect protein levels, illness, pituitary problems, and hypothyroidism.',
            symptoms: [
                'Weight gain',
                'Fatigue',
                'Hair loss',
                'Low tolerance for cold temperatures',
                'Irregular period',
                'Constipation',
            ],
        },
    },
};

// Helper function to get education for a test
export const getTestEducation = (testName: string): TestEducation | null => {
    if (!testName) return null;

    const normalizedName = testName.toLowerCase().trim();

    // Direct match
    if (TEST_EDUCATION[normalizedName]) {
        return TEST_EDUCATION[normalizedName];
    }

    // Special cases for common test name variations
    if (
        normalizedName.includes('tpoab') ||
        normalizedName.includes('tpo ab') ||
        normalizedName.includes('thyroid peroxidase')
    ) {
        return TEST_EDUCATION['tpoab'];
    }

    if (normalizedName.includes('tsh') || normalizedName === 'thyroid stimulating hormone') {
        return TEST_EDUCATION['tsh'];
    }

    if (normalizedName.includes('free t3') || normalizedName === 't3, free') {
        return TEST_EDUCATION['free t3'];
    }

    if (normalizedName.includes('free t4') || normalizedName === 't4, free') {
        return TEST_EDUCATION['free t4'];
    }

    if (normalizedName.includes('t4, total') || normalizedName === 'total t4') {
        return TEST_EDUCATION['t4, total'];
    }

    if (normalizedName.includes('hemoglobin a1c') || normalizedName.includes('hba1c')) {
        return TEST_EDUCATION['hba1c'];
    }

    if (normalizedName.includes('vitamin d') || normalizedName.includes('25-hydroxy')) {
        return TEST_EDUCATION['vitamin d (oh-25)'];
    }

    if (normalizedName.includes('ldl') || normalizedName.includes('low density')) {
        return TEST_EDUCATION['ldl'];
    }

    if (normalizedName.includes('hdl') || normalizedName.includes('high density')) {
        return TEST_EDUCATION['direct hdl'];
    }

    if (normalizedName.includes('estradiol')) {
        return TEST_EDUCATION['estradiol'];
    }

    if (normalizedName.includes('testosterone')) {
        if (normalizedName.includes('free')) {
            return TEST_EDUCATION['free testosterone'];
        }
        if (normalizedName.includes('total')) {
            return TEST_EDUCATION['total testosterone'];
        }
        // Default to total testosterone for generic "testosterone"
        return TEST_EDUCATION['total testosterone'];
    }

    if (normalizedName.includes('iron')) {
        return TEST_EDUCATION['iron'];
    }

    // Partial matches for common variations
    for (const [key, education] of Object.entries(TEST_EDUCATION)) {
        if (normalizedName.includes(key) || key.includes(normalizedName)) {
            return education;
        }
    }

    return null;
};
